import * as React from 'react';
import {
  CRUCIBLE_INSPECT_MARKER,
  INSPECT_DESCRIPTOR_TEXT_CAP,
  INSPECT_DESCRIPTOR_MAX_CLASSES,
  INSPECT_DRAG_THRESHOLD,
} from '@volley/crucible-ipc-types/preview-inspect-constants';
import {
  buildArm,
  buildDisarm,
  parseInspectEvent,
  isTrustedInspectSource,
} from '../preview/inspect-protocol';
import { buildInspectorScript } from '../preview/inspector-script';
import {
  scalePaneRect,
  formatSelectionContext,
  type PreviewSelectionContext,
} from '../preview/select-area';

/**
 * Renderer-side driver for the unified select / pause mode
 * (select-to-chat, `docs/design/preview-select-to-chat-prd.md`).
 *
 * One global Pause/Resume:
 *   - `enter()` (Pause) asks MAIN to inject the inspector into EVERY
 *     preview frame (`preview:inject-inspector`), then arms each
 *     preview iframe (TV + every controller) by posting `arm` to its
 *     `contentWindow`. Each screen soft-pauses and becomes selectable.
 *   - `exit()` (Resume) disarms every screen.
 *
 * The inspector owns all in-screen interaction (hover, click-to-select,
 * double-click-to-drill, drag-to-area, the in-iframe "Add to chat"
 * button). It crosses the boundary only on `ready` and `selection`. On
 * `selection` this hook attributes the source iframe (by
 * `contentWindow` match), reads its `data-crucible-surface`, maps the
 * rect to window px via the iframe's client rect, captures the pixels
 * (`preview:capture-area`), and routes a surface-aware context block to
 * chat. Pause stays active so the user can make several selections.
 *
 * Capability gate: injection returns the count of preview frames found;
 * `0` ⇒ preview not open ⇒ pause unavailable (the button stays
 * inactive). Renderer-only; the parsing/trust/geometry it leans on is
 * the pure, unit-tested protocol + select-area helpers.
 */

export interface UsePreviewInspectorArgs {
  /** Scope to find preview iframes within (the preview body). */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Whether pause/select is applicable (Electron bridge present). */
  enabled: boolean;
  /** Deliver a captured selection to chat (same pipeline as paste). */
  onAddSelectionToChat: (payload: { file: File; contextBlock: string }) => void;
  /** Surface a user-facing error (e.g. the capture failed after the
   *  inspector already cleared the framed selection). */
  onSelectionError?: (message: string) => void;
  /**
   * Where the captured-selection ghost flies on its way to chat:
   *   - `'composer'` (default): down to the chat composer on the left —
   *     used by the in-editor (Develop split) preview, where the chat is
   *     visible and the attachment thumbnail pops up there.
   *   - `'chat-tab'`: up to the Develop tab pill in the header — used by
   *     the full-bleed Preview mode, where the chat panel isn't visible.
   */
  flyTo?: 'composer' | 'chat-tab';
}

export interface UsePreviewInspector {
  /** True while the preview is paused (select mode active). */
  paused: boolean;
  /** Pause: inject + arm all screens. Resolves false when no preview
   *  frame was found (button stays inactive). */
  enter: () => Promise<boolean>;
  /** Resume: disarm all screens. */
  exit: () => void;
}

function previewIframes(container: HTMLElement | null): HTMLIFrameElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLIFrameElement>('iframe[data-crucible-surface]'));
}

function frameOrigin(iframe: HTMLIFrameElement): string | null {
  try {
    return new URL(iframe.src).origin;
  } catch {
    return null;
  }
}

export function usePreviewInspector(args: UsePreviewInspectorArgs): UsePreviewInspector {
  const { containerRef, enabled, onAddSelectionToChat, onSelectionError, flyTo } = args;
  const [paused, setPaused] = React.useState(false);
  const flyToRef = React.useRef<'composer' | 'chat-tab'>(flyTo ?? 'composer');
  flyToRef.current = flyTo ?? 'composer';

  const pausedRef = React.useRef(false);
  pausedRef.current = paused;
  const enabledRef = React.useRef(enabled);
  enabledRef.current = enabled;
  const mountedRef = React.useRef(true);
  const enterGenerationRef = React.useRef(0);
  const onAddRef = React.useRef(onAddSelectionToChat);
  onAddRef.current = onAddSelectionToChat;
  const onErrorRef = React.useRef(onSelectionError);
  onErrorRef.current = onSelectionError;
  // Stable handle to `exit` for the mount-once Esc / message listeners
  // (assigned below, once `exit` is defined).
  const exitRef = React.useRef<() => void>(() => {});

  const armFrame = React.useCallback((iframe: HTMLIFrameElement) => {
    const origin = frameOrigin(iframe);
    const win = iframe.contentWindow;
    if (!origin || !win) return;
    win.postMessage(buildArm(), origin);
  }, []);

  const disarmAll = React.useCallback(() => {
    for (const iframe of previewIframes(containerRef.current)) {
      const origin = frameOrigin(iframe);
      const win = iframe.contentWindow;
      if (origin && win) win.postMessage(buildDisarm(), origin);
    }
  }, [containerRef]);

  const handleSelection = React.useCallback(
    async (iframe: HTMLIFrameElement, event: ReturnType<typeof parseInspectEvent>) => {
      if (!event || event.__crucibleInspect !== 'selection') return;
      // Async capture/animation must not deliver after exit or iframe remount.
      const selectionGeneration = enterGenerationRef.current;
      const isCurrentSelection = () =>
        mountedRef.current &&
        pausedRef.current &&
        enabledRef.current &&
        enterGenerationRef.current === selectionGeneration &&
        previewIframes(containerRef.current).includes(iframe);
      const surface = iframe.getAttribute('data-crucible-surface') || 'tv';
      const client = iframe.getBoundingClientRect();
      if (client.width <= 0 || client.height <= 0) {
        console.error('[preview] selection capture skipped: preview frame has no size', {
          width: client.width,
          height: client.height,
          surface,
        });
        onErrorRef.current?.("Couldn't capture that selection - try selecting again.");
        return;
      }
      // Inspector rect (iframe-viewport px) → display-box px, then offset
      // by the iframe's window position → window px for capture.
      const boxRect = scalePaneRect(
        event.rect,
        event.viewport.width,
        event.viewport.height,
        client.width,
        client.height,
      );
      if (!boxRect) {
        console.error('[preview] selection capture skipped: invalid selection geometry', {
          rect: event.rect,
          viewport: event.viewport,
          surface,
        });
        onErrorRef.current?.("Couldn't capture that selection - try selecting again.");
        return;
      }
      const windowRect = {
        x: client.left + boxRect.x,
        y: client.top + boxRect.y,
        width: boxRect.width,
        height: boxRect.height,
      };
      let file: File;
      let dataUrl: string;
      try {
        const result = await window.crucible.invoke('preview:capture-area', { rect: windowRect });
        if (!isCurrentSelection()) return;
        file = base64ToFile(result.base64, result.mimeType, 'preview-selection.png');
        dataUrl = `data:${result.mimeType};base64,${result.base64}`;
      } catch (err) {
        if (!isCurrentSelection()) return;
        // The inspector already cleared the framed selection on the
        // Add-to-chat click, so a silent return would make the selection
        // vanish with nothing attached. Surface a toast so the user
        // knows to retry (cursor[bot] on #1877).
        console.error('[preview] selection capture failed', err);
        onErrorRef.current?.("Couldn't capture that selection - try selecting again.");
        return;
      }
      const context: PreviewSelectionContext = {
        kind: event.kind,
        surface,
        // The inspector rect IS surface space; surfaceSize == its viewport.
        rect: event.rect,
        surfaceSize: event.viewport,
        ...(event.kind === 'element' && event.descriptor
          ? { element: { ...event.descriptor } }
          : {}),
        ...(event.screenText ? { screenText: event.screenText } : {}),
      };
      // Fly the captured crop toward chat, THEN deliver it. In-editor preview
      // flies to the composer (the thumbnail pops up there); full Preview mode
      // flies to the Develop tab in the header (chat panel not visible).
      await (flyToRef.current === 'chat-tab'
        ? flyCaptureToChatTab(dataUrl, windowRect)
        : flyCaptureToComposer(dataUrl, windowRect));
      if (!isCurrentSelection()) return;
      onAddRef.current({ file, contextBlock: formatSelectionContext(context) });
    },
    [],
  );

  React.useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!pausedRef.current) return;
      // Attribute the message to one of our preview iframes by
      // contentWindow identity, then trust-check its origin.
      const iframe = previewIframes(containerRef.current).find(
        (f) => f.contentWindow === event.source,
      );
      if (!iframe) return;
      if (
        !isTrustedInspectSource({
          eventSource: event.source,
          eventOrigin: event.origin,
          iframeContentWindow: iframe.contentWindow,
          frameOrigin: frameOrigin(iframe),
        })
      ) {
        return;
      }
      const parsed = parseInspectEvent(event.data);
      if (!parsed) return;
      if (parsed.__crucibleInspect === 'ready') {
        // A frame (re)announced — arm it (covers reload re-injection).
        armFrame(iframe);
      } else if (parsed.__crucibleInspect === 'selection') {
        void handleSelection(iframe, parsed);
      } else if (parsed.__crucibleInspect === 'exit') {
        // Esc bottomed out inside a focused iframe → leave select mode.
        exitRef.current();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [containerRef, armFrame, handleSelection]);

  // Esc with focus on the PARENT (e.g. right after clicking Select,
  // before touching a screen) — leave select mode. When a preview
  // iframe has focus the parent never sees the Esc, so the inspector's
  // own ladder (drill out → clear selection → post `exit`) handles it
  // there; the two paths are focus-exclusive.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !pausedRef.current) return;
      // If a preview iframe holds focus, ITS inspector owns this Esc
      // (drill out → clear → post `exit`); don't pre-empt it by exiting
      // here. Only act when focus is on the parent (e.g. just after
      // clicking Select, or hovering without interacting).
      const active = document.activeElement;
      if (active instanceof HTMLIFrameElement && active.hasAttribute('data-crucible-surface')) {
        return;
      }
      e.preventDefault();
      exitRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const enter = React.useCallback(async (): Promise<boolean> => {
    if (!enabledRef.current) return false;
    if (typeof window === 'undefined' || typeof window.crucible?.invoke !== 'function') {
      return false;
    }
    const generation = enterGenerationRef.current + 1;
    enterGenerationRef.current = generation;
    const isCurrentEnter = () =>
      mountedRef.current && enterGenerationRef.current === generation && enabledRef.current;
    const script = buildInspectorScript({
      marker: CRUCIBLE_INSPECT_MARKER,
      textCap: INSPECT_DESCRIPTOR_TEXT_CAP,
      maxClasses: INSPECT_DESCRIPTOR_MAX_CLASSES,
      dragThreshold: INSPECT_DRAG_THRESHOLD,
    });
    let injected = 0;
    try {
      const result = await window.crucible.invoke('preview:inject-inspector', { script });
      injected = result.injected;
    } catch (err) {
      if (!isCurrentEnter()) return false;
      console.error('[preview] inspector injection failed', err);
      onErrorRef.current?.("Couldn't start select mode — try again in a moment.");
      return false;
    }
    if (!isCurrentEnter()) return false;
    if (injected <= 0) {
      // Main found no preview child frames (preview not in the frame
      // tree yet / not open). Tell the user instead of looking broken.
      onErrorRef.current?.('Open the preview first, then try Select again.');
      return false;
    }
    // Only enter select mode if we can actually reach armable preview
    // iframes — otherwise the toolbar would show "selecting" while the
    // previews stay live and unpaused.
    const frames = previewIframes(containerRef.current);
    if (frames.length === 0) {
      onErrorRef.current?.("Couldn't reach the preview to select — try again in a moment.");
      return false;
    }
    setPaused(true);
    pausedRef.current = true;
    // The injected scripts post `ready`; the listener arms each on it.
    // Arm proactively too in case `ready` landed before paused flipped.
    for (const iframe of frames) armFrame(iframe);
    return true;
  }, [containerRef, armFrame]);

  const exit = React.useCallback(() => {
    enterGenerationRef.current += 1;
    disarmAll();
    setPaused(false);
    pausedRef.current = false;
  }, [disarmAll]);
  exitRef.current = exit;

  // Disarm on unmount (e.g. leaving the Preview tab while select mode
  // is active) so a still-attached preview iframe doesn't keep its
  // crosshair overlay + blocked input.
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      enterGenerationRef.current += 1;
      disarmAll();
    };
  }, [disarmAll]);

  return { paused, enter, exit };
}

/** Decode a base64 image payload into a File for the attachment
 *  pipeline (capture returns base64 from `capturePage`). */
function base64ToFile(base64: string, mimeType: string, name: string): File {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], name, { type: mimeType });
}

/**
 * "Fly to Develop tab" — animate a shrinking copy of the captured crop up to
 * the Develop tab pill (`[data-chat-tab]` in the header), then resolve. Used by
 * the full-bleed Preview mode, where the chat composer isn't on screen.
 */
function flyCaptureToChatTab(
  dataUrl: string,
  from: { x: number; y: number; width: number; height: number },
): Promise<void> {
  return new Promise<void>((resolve) => {
    if (typeof document === 'undefined') return resolve();
    const target = document.querySelector('[data-chat-tab]');
    const ghost = document.createElement('img');
    if (!target || typeof ghost.animate !== 'function' || from.width <= 0 || from.height <= 0) {
      return resolve();
    }
    const t = target.getBoundingClientRect();
    ghost.src = dataUrl;
    Object.assign(ghost.style, {
      position: 'fixed',
      left: `${from.x}px`,
      top: `${from.y}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
      objectFit: 'cover',
      borderRadius: '8px',
      border: '1px solid rgba(255,255,255,0.25)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
      zIndex: '2147483647',
      pointerEvents: 'none',
      margin: '0',
    } as CSSStyleDeclaration);
    document.body.appendChild(ghost);
    const dx = t.left + t.width / 2 - (from.x + from.width / 2);
    const dy = t.top + t.height / 2 - (from.y + from.height / 2);
    const scale = Math.min(0.18, 28 / Math.max(from.width, from.height));
    const anim = ghost.animate(
      [
        { transform: 'translate(0px, 0px) scale(1)', opacity: 1 },
        { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0.15 },
      ],
      { duration: 600, easing: 'cubic-bezier(0.5, 0, 0.2, 1)', fill: 'forwards' },
    );
    const done = () => {
      ghost.remove();
      resolve();
    };
    anim.addEventListener('finish', done);
    anim.addEventListener('cancel', done);
  });
}

/**
 * "Fly to composer" — animate a shrinking copy of the captured crop from
 * its on-screen rect down to the chat composer (`.chat-composer-surface`,
 * on the LEFT in the Develop split), where the attachment thumbnail then
 * pops up. A clear "your selection is heading to the chat" cue. Resolves
 * immediately (no animation) when the target or the Web Animations API
 * isn't available, so the caller never blocks.
 */
function flyCaptureToComposer(
  dataUrl: string,
  from: { x: number; y: number; width: number; height: number },
): Promise<void> {
  return new Promise<void>((resolve) => {
    if (typeof document === 'undefined') return resolve();
    // The mounted chat composer (pill or expanded — only one is in the DOM).
    const target = document.querySelector('.chat-composer-surface');
    const ghost = document.createElement('img');
    if (!target || typeof ghost.animate !== 'function' || from.width <= 0 || from.height <= 0) {
      return resolve();
    }
    const t = target.getBoundingClientRect();
    ghost.src = dataUrl;
    Object.assign(ghost.style, {
      position: 'fixed',
      left: `${from.x}px`,
      top: `${from.y}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
      objectFit: 'cover',
      borderRadius: '8px',
      border: '1px solid rgba(255,255,255,0.25)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
      zIndex: '2147483647',
      pointerEvents: 'none',
      margin: '0',
    } as CSSStyleDeclaration);
    document.body.appendChild(ghost);
    // Move the crop toward the TOP-LEFT of the composer (where the attachment
    // chip pops up, just above the input) and shrink it to a thumbnail;
    // transform-based so it stays smooth.
    const landX = t.left + Math.min(40, t.width / 2);
    const landY = t.top - 4;
    const dx = landX - (from.x + from.width / 2);
    const dy = landY - (from.y + from.height / 2);
    const scale = Math.min(0.4, 44 / Math.max(from.width, from.height));
    const anim = ghost.animate(
      [
        { transform: 'translate(0px, 0px) scale(1)', opacity: 1 },
        {
          transform: `translate(${dx}px, ${dy}px) scale(${scale})`,
          opacity: 0.1,
        },
      ],
      { duration: 600, easing: 'cubic-bezier(0.5, 0, 0.2, 1)', fill: 'forwards' },
    );
    const done = () => {
      ghost.remove();
      resolve();
    };
    anim.addEventListener('finish', done);
    anim.addEventListener('cancel', done);
  });
}
